import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import SignReceiptSideMsg from '#/modules/crmMgmt/aftersales/signReceipt/components/SignReceiptSideMsg';
import { buildSignReceiptColumns } from '#/modules/crmMgmt/aftersales/signReceipt/data/signReceiptColumns';
import {
  getSignReceiptApiErrorMessage,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/apiError';
import {
  applySignReceiptDateRangeToFilters,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/signReceiptDateListFilter';
import useSignReceiptList from '#/modules/crmMgmt/aftersales/signReceipt/hooks/useSignReceiptList';
import {
  hydrateCustomerFilterLabels,
} from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  signReceiptNo: '簽收單號',
  status: '狀態',
  signReceiptDate: '預計送交日',
  customerName: '客戶',
};

const SignReceiptListPage = () => {
  const canCreate = useCan(PERM.SIGN_RECEIPT_CREATE);
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const [customerLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useSignReceiptList();

  useEffect(() => {
    let cancelled = false;

    hydrateCustomerFilterLabels(listQuery.filters?.customerId, customerLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.customerId,
    customerLabelCache,
  ]);

  // 起迄須同一次寫入 URL；連續兩次 setFilter 會以舊 listQuery 為底，後者蓋掉前者
  const setSignReceiptDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applySignReceiptDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'signReceiptDateFrom' && value === undefined) {
      setSignReceiptDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setSignReceiptDateRangeFilter]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildSignReceiptColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onSignReceiptDateRangeChange: setSignReceiptDateRangeFilter,
        customerLabelCache,
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setSignReceiptDateRangeFilter,
      customerLabelCache,
      labelCacheVersion,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }
    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate
    ? <ListAddButton to={ROUTES.signReceiptCreate} />
    : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <AftersalesOpsPageHeader page="signReceipt" variant="list" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getSignReceiptApiErrorMessage(error, '載入列表失敗（API 可能尚未部署）')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋簽收單號、客戶或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={headerAction}
            />
          </div>
          <SignReceiptSideMsg displayData={displayData} />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default SignReceiptListPage;
